import MatchEnum from "~src/enums/MatchEnum";
import MatchBasketballEnum from "~src/enums/basketball/MatchEnum";
import type {
  IMatch,
  IMatchLive,
  ICompetitions,
  ITeam,
  ILeague,
  IStage,
  IRound,
  IItemString,
  ILineups,
  IIncident,
  IPlayer,
  IStatistics,
  IStatsJsonData,
  IShootingPoints,
  IShootingPointResponseData,
  IPlayerDataShooting,
} from "~src/interfaces/basketball";
import StatsEnum from "~src/enums/basketball/StatsEnum";

export const parseMatchData = (matchDataString: string): Array<IMatch> => {
  if (!matchDataString) {
    return [];
  }

  const matchDataArray = matchDataString.split("!!");
  const matches = matchDataArray.map((matchString) => {
    const matchDetails = matchString.split("^");

    const agg_score = matchDetails[35] ? matchDetails[35].split("-") : [];

    return {
      id: matchDetails[0],
      match_time: matchDetails[1] as unknown as number,
      half_start_time: matchDetails[2],
      competition_id: matchDetails[3],
      season_id: matchDetails[4],
      status_id: parseInt(matchDetails[5]),
      home_team_id: matchDetails[6],
      home_team: {
        id: matchDetails[6],
        name: matchDetails[7],
      } as unknown as ITeam,
      home_scores: [
        parseInt(matchDetails[8]),
        parseInt(matchDetails[9]),
        parseInt(matchDetails[10]),
        parseInt(matchDetails[11]),
        parseInt(matchDetails[12]),
        parseInt(matchDetails[13]),
        parseInt(matchDetails[14]),
      ],
      home_position: matchDetails[15],
      away_team_id: matchDetails[16],
      away_team: {
        id: matchDetails[16],
        name: matchDetails[17],
      } as unknown as ITeam,
      away_scores: [
        parseInt(matchDetails[18]),
        parseInt(matchDetails[19]),
        parseInt(matchDetails[20]),
        parseInt(matchDetails[21]),
        parseInt(matchDetails[22]),
        parseInt(matchDetails[23]),
        parseInt(matchDetails[24]),
      ],
      away_position: matchDetails[25],
      round: {
        stage_id: matchDetails[26],
        group_num: matchDetails[27],
        round_num: matchDetails[28],
      } as unknown as IRound,
      stage: {
        id: matchDetails[29],
        name: matchDetails[30],
        mode: matchDetails[31] as unknown as number,
        group_count: matchDetails[32] as unknown as number,
        round_count: matchDetails[33] as unknown as number,
        order: matchDetails[34] as unknown as number,
      } as unknown as IStage,
      agg_score: agg_score as unknown as Array<number>,
      leg: matchDetails[36] ?? "",
    } as unknown as IMatch;
  });

  return matches;
};

export const parseCompetitionData = (
  competitionDataString: string
): Array<ICompetitions> => {
  if (!competitionDataString) {
    return [];
  }

  const competitionDataArray = competitionDataString.split("!!");
  const competitions = competitionDataArray.map((competitionString) => {
    const competitionDetails = competitionString.split("^");

    return {
      id: competitionDetails[0],
      name: competitionDetails[1],
      short_name: competitionDetails[2],
      type: competitionDetails[3] ? parseInt(competitionDetails[3]) : 0,
      popularity: competitionDetails[4] ? parseInt(competitionDetails[4]) : 0,
      primary_color: competitionDetails[5],
      secondary_color: competitionDetails[6],
      cur_season_id: competitionDetails[8],
    } as unknown as ICompetitions;
  });

  return competitions;
};

export const parseMatchH2hData = (
  item: { [key: number]: any },
  homeTeamId: string
) => {
  const listStatusPlaying = MatchBasketballEnum.LIST_STATUS_PLAYING;

  const statusEnd = MatchBasketballEnum.STATUS_END;

  const status_id = item[3];
  const Kick_off_time = item[5];
  const home_team_id = item[6][0];
  const away_team_id = item[7][0];
  const home_scores = item[6].slice(2, item[6].length);
  const away_scores = item[7].slice(2, item[7].length);

  let matchTimePlayer = null;
  if (listStatusPlaying.includes(status_id)) {
    matchTimePlayer = getTimeDetailLive(status_id, Kick_off_time);
  } else if (status_id === statusEnd) {
    // matchTimePlayer = getResultText(status_id, home_scores, away_scores);

    const status = determineMatchOutcome(item, homeTeamId);
    let statusTxt = "";
    if (status === "win") {
      statusTxt = "Thắng";
    } else if (status === "draw") {
      statusTxt = "Hòa";
    } else {
      statusTxt = "Thua";
    }

    matchTimePlayer = {
      text: statusTxt,
      status_h2h: status,
    };
  }

  return {
    id: item[0],
    competition_id: item[2],
    status_id,
    match_time: item[4],
    Kick_off_time,
    home_scores,
    away_scores,
    home_team_id,
    away_team_id,
    match_time_player: matchTimePlayer,
  };
};

export const getTotalScore = (match: IMatch) => {
  const home_scores = match.home_scores;
  const away_scores = match.away_scores;

  let homeScore = 0;
  let awayScore = 0;
  let homeWin = 0;
  let awayWin = 0;
  home_scores.forEach((score: number, index: number) => {
    homeScore += score;
    awayScore += away_scores[index] ? away_scores[index] : away_scores[index];
    if (score > away_scores[index]) {
      homeWin++;
    } else {
      awayWin++;
    }
  });

  return {
    homeScore,
    awayScore,
    homeWin,
    awayWin,
  };
};

export const determineMatchOutcome = (
  matchData: { [key: number]: any },
  homeTeamId: string
) => {
  const home_team_id = matchData[6][0];
  const away_team_id = matchData[7][0];
  const home_scores = matchData[6].slice(2, matchData[6].length);
  const away_scores = matchData[7].slice(2, matchData[7].length);

  const totalHomeGoals = home_scores.reduce(
    (partialSum: number, a: number) => partialSum + a,
    0
  );

  const totalAwayGoals = away_scores.reduce(
    (partialSum: number, a: number) => partialSum + a,
    0
  );

  let homeWinTotal = 0;
  let awayWinTotal = 0;
  home_scores.forEach((score: number, index: number) => {
    if (score > away_scores[index]) {
      homeWinTotal++;
    } else if (score < away_scores[index]) {
      awayWinTotal++;
    }
  });

  const isActualHomeTeam = home_team_id === homeTeamId;
  if (totalHomeGoals > totalAwayGoals) {
    return isActualHomeTeam ? "win" : "lose"; // Kiểm tra đội nhà có phải là đội mình quan tâm không
  } else if (totalHomeGoals === totalAwayGoals) {
    return "draw"; // Hòa
  } else {
    return isActualHomeTeam ? "lose" : "win"; // Đảo ngược kết quả nếu đội mình quan tâm không phải là đội nhà
  }
};

export const determineMatchOutcome1 = (match: IMatch, homeTeamId: string) => {
  const totalHomeGoals = match.home_scores[0];
  const totalAwayGoals = match.away_scores[0];

  const isActualHomeTeam = match.home_team?.id === homeTeamId;
  // Xác định trạng thái trận đấu dựa trên tổng số bàn thắng
  if (totalHomeGoals > totalAwayGoals) {
    return isActualHomeTeam ? "win" : "lose"; // Kiểm tra đội nhà có phải là đội mình quan tâm không
  } else if (totalHomeGoals === totalAwayGoals) {
    return "draw"; // Hòa
  } else {
    return isActualHomeTeam ? "lose" : "win"; // Đảo ngược kết quả nếu đội mình quan tâm không phải là đội nhà
  }
};

export const determineMatchResult = (match: IMatch, teamId: string) => {
  const totalHomeGoals = match.home_scores
    .map((score) => score)
    .reduce((a, b) => a + b, 0);
  const totalAwayGoals = match.away_scores
    .map((score) => score)
    .reduce((a, b) => a + b, 0);
  if (match.home_team?.id === teamId) {
    if (totalHomeGoals > totalAwayGoals) return "w";
    if (totalHomeGoals < totalAwayGoals) return "l";
  } else if (match.away_team?.id === teamId) {
    if (totalHomeGoals < totalAwayGoals) return "w";
    if (totalHomeGoals > totalAwayGoals) return "l";
  }
  return "d";
};

export const determineMatchResultVolleyball = (match: any , teamId: string) => {
  const scores = match?.scores;
  const totalHomeGoals = scores['ft'][0];
  const totalAwayGoals = scores['ft'][1];

  if (match.home_team?.id === teamId) {
    if (totalHomeGoals > totalAwayGoals) return "w";
    if (totalHomeGoals < totalAwayGoals) return "l";
  } else if (match.away_team?.id === teamId) {
    if (totalAwayGoals > totalHomeGoals) return "w";
    if (totalAwayGoals < totalHomeGoals) return "l";
  }
  return "d";
};

const getCurrentDateTimestamp = () => {
  const currentDateTimestamp = Math.floor(Date.now() / 1000);

  return currentDateTimestamp;
};

const timestampToMinutesSeconds = (timestamp: number) => {
  const minutes = Math.floor(timestamp / 60);
  const seconds = timestamp % 60;

  return { minutes, seconds };
};

export const geTimePassedBK = (
  remaining: number,
  quarters: number = 0,
  time: number = 12
) => {
  return quarters + (time - remaining);
};

export const geTimePassed = (
  rounds: number = 0,
  time_in_set: number = 12,
  remaining: number = 0,
) => {
  return rounds * time_in_set + (time_in_set - remaining);
};

export const getTimeDetailLive = (statusId: number, startTimestamp = null, competition: any = null) => {
  const stautsText = MatchBasketballEnum.MATCH_STATUS_TEXT as {
    [key: string]: any;
  };

  const statusTxt = stautsText[statusId] || "";
  let timePlayerProcess = 0;

  if (!startTimestamp) {
    return {
      text: statusTxt,
      process: timePlayerProcess,
    };
  }
  const { minutes } = timestampToMinutesSeconds(startTimestamp);
  // % thời gian đá
  let total_times = 48;
  let time_in_set = 12;
  let extra_time = 5;
  let round_ot = 3;

  // Bóng rổ cấp độ đại học (NCAA - Mỹ)
  // Số hiệp: 2 hiệp.
  // Thời gian mỗi hiệp: 20 phút.
  // Thời gian nghỉ giữa 2 hiệp: 15 phút.
  if(competition && competition?.id){
    switch(competition?.id){
      case '9vjxm8xtl5q6odg':
        total_times = 40;
        time_in_set = 20;
        round_ot = 1;
        break;
    }
  }

  const timeProcess = {
    2: (geTimePassed(0, time_in_set, minutes) / total_times) * 100,
    3: 25,
    4: (geTimePassed(1, time_in_set, minutes) / total_times) * 100,
    5: 50,
    6: (geTimePassed(2, time_in_set, minutes) / total_times) * 100,
    7: 75,
    8: (geTimePassed(3, time_in_set, minutes) / total_times) * 100,
    9: ((geTimePassed(round_ot, time_in_set, minutes) + extra_time) / (total_times + extra_time)) * 100,
  } as { [key: number]: any };

  // const statusTimeTexts = {
  //   2: geTimePassed(minutes),
  //   4: geTimePassed(minutes, 12),
  //   6: geTimePassed(minutes, 24),
  //   8: geTimePassed(minutes, 36),
  //   9: geTimePassed(minutes, 48),
  // } as { [key: number]: any };

  // const timeText = statusTimeTexts[statusId]
  //   ? `: ${statusTimeTexts[statusId]}`
  //   : "";

  timePlayerProcess = timeProcess[statusId] ? timeProcess[statusId] : 0;

  return {
    text: statusId !== 9 ? `${statusTxt}: ${minutes}'` : statusTxt,
    process: timePlayerProcess,
  };
};

export const getTimeUtc = (time: any) => {
  const utcDateString = time.toUTCString();

  return new Date(utcDateString).getTime() / 1000;
};

export const unReverseId = (id: number) => {
  if (!id) {
    return id;
  }

  const sum = id
    .toString()
    .split("")
    .reduce((acc, digit) => acc + parseInt(digit, 10), 0);
  const firstNumber = sum < 10 ? 1 : parseInt(sum.toString()[0], 10);
  const lastNumber = sum < 10 ? sum : parseInt(sum.toString().slice(-1), 10);

  return parseInt(
    firstNumber + id.toString().split("").reverse().join("") + lastNumber
  );
};

export const addMatchDetailLive = async (
  matchs: Array<IMatch>,
  detailLive: Array<IMatchLive>,
  competitions: any[]
) => {
  const datas = [];
  for (const match of matchs) {
    const matchDetail = detailLive.find((item) => match.id === item.id);
    const competition = competitions.find((competition: any) => competition.id === match.competition_id);
    let newdata = {};
    if (matchDetail) {
      const status_id = matchDetail.score[1];
      const kickOffTime = matchDetail.timer[3];
      newdata = {
        matchLive: matchDetail,
        match_time_player: getTimeDetailLive(status_id, kickOffTime, competition),
        status_id: status_id,
        home_scores: matchDetail.score[3],
        away_scores: matchDetail.score[4],
      };
    } else {
      newdata = {
        matchLive: null,
        match_time_player: getTimeDetailLive(match.status_id, null, competition),
      };
    }
    match.competition = competition;
    datas.push({
      ...match,
      ...newdata,
    });
  }

  return datas;
};

export const getRoundName = (match: IMatch, type: number | undefined) => {
  if (match.stage) {
    if (type === MatchEnum.STAGE_MODE_LEAGUE) {
      return match.round?.round_num && match.round?.round_num != 0
        ? `Vòng ${match.round.round_num}`
        : "";
    } else if (type === MatchEnum.STAGE_MODE_CUP) {
      // return match.stage.name ? match.stage.name.replace("Round", "Vòng") : "";

      const stage = match.stage;
      const round = match.round as IRound;
      const stageTxts = {
        Group: "Bảng",
        Round: "Vòng",
        "Qualifying Round": "Vòng loại",
        "Group Stage": "Bảng",
        "1/8 Final": "Vòng 1/8",
        "Quarter-finals": "Tứ kết",
        "Quarter Final": "Tứ kết",
        "Semi-finals": "Bán kết",
        Semifinal: "Bán kết",
        "Third Place": "Trận tranh hạng 3",
        Final: "Chung kết",
      } as IItemString;

      const arr = [];
      let stgName: string = stage.name_vi ? stage.name_vi : stage.name;
      if (stageTxts[stgName]) {
        stgName = stageTxts[stgName];
      } else {
        stgName = stgName.replace("Round", "Vòng");
      }
      arr.push(stgName);

      const groups = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J"];
      if (round["group_num"] && round["group_num"] != 0) {
        arr.push(groups[round["group_num"] - 1]);
      }

      const roundNum = round["round_num"];
      if (roundNum && roundNum != 0) {
        arr.push(`- Vòng ${roundNum}`);
      }

      return arr.join(" ");
    }
  }

  return "";
};

export const getTeamName = (teams: ITeam[], teamId: string) => {
  const team: any = teams.find((team: ITeam) => team.id === teamId);

  if (team) {
    if (team.short_name_vi) {
      return team.short_name_vi;
    } else if (team.short_name) {
      return team.short_name;
    } else {
      return team.name;
    }
  }

  return "";
};

export const getCompetitionName = (
  competitions: ILeague[],
  competitionId: string
) => {
  const competition: any = competitions.find(
    (competition: ILeague) => competition.id === competitionId
  );

  if (competition) {
    if (competition.short_name_vi) {
      return competition.short_name_vi;
    } else if (competition.short_name) {
      return competition.short_name;
    } else {
      return competition.name;
    }
  }

  return "";
};

export const addIncidentToPlayer = (
  players: IPlayer[],
  incidents: IIncident[]
) => {
  let i = 0;
  for (const player of players) {
    // Tìm toàn bộ incidents của player
    const incidentPlayers = incidents.filter((item: IIncident) => {
      if (
        (item.player && item.player.id === player.id) ||
        (item.playerIn && item.playerIn.id === player.id) ||
        (item.playerOut && item.playerOut.id === player.id)
      ) {
        return true;
      }

      return false;
    });
    if (incidentPlayers.length) {
      // gán incident vào player
      for (const incident of incidentPlayers) {
        const check = player.incidents.find(
          (inci: IIncident) => inci.time == incident.time
        );
        if (!check) {
          if (incident.playerIn) {
            incident.in_player = incident.playerIn;
          }
          if (incident.playerOut) {
            incident.out_player = incident.playerOut;
          }
          players[i].incidents.push(incident);
        }
      }
    }
    i++;
  }

  return players;
};

const calculatePercentage = (value: number, total: number): string => {
  if (total === 0) return "0%";
  return `${((value / total) * 100).toFixed(2)}%`;
};
const chooseValue = (
  statistics: IStatistics | any,
  key: string,
  index: number
) => {
  let team = index === 1 ? "home" : "away";
  let teamData = index === 1 ? "homeData" : "awayData";
  let data;
  if (key === "points") {
    data = statistics.score[team];
  }

  if (key === "hits_shots") {
    data =
      statistics[teamData].hits_shots !== 0
        ? `${statistics[teamData].hits_shots}/${statistics[teamData].total_hits_shots}`
        : statistics[teamData].hits_shots;
  }

  if (key === "max_hits_shots") {
    data = statistics[teamData].hits_shots;
  }

  if (key === "hits_shots_percentage") {
    data = `${statistics[teamData].hits_shots_percentage}%`;
  }

  if (key === "hits_shots_percentage_number") {
    data = statistics[teamData].hits_shots_percentage;
  }

  if (key === "three_point") {
    data =
      statistics[teamData].total_three_point !== 0
        ? `${statistics[teamData].three_point}/${statistics[teamData].total_three_point}`
        : statistics[teamData].three_point;
  }

  if (key === "max_three_point") {
    data = statistics[teamData].three_point;
  }

  if (key === "three_point_percentage") {
    data = `${statistics[teamData].three_point_percentage}%`;
  }

  if (key === "three_point_percentage_number") {
    data = statistics[teamData].three_point_percentage;
  }

  if (key === "free_throw_goal") {
    data =
      statistics[teamData].total_free_throw_goal !== 0
        ? `${statistics[teamData].free_throw_goal}/${statistics[teamData].total_free_throw_goal}`
        : statistics[teamData].free_throw_goal;
  }

  if (key === "max_free_throw_goal") {
    data = statistics[teamData].free_throw_goal;
  }

  if (key === "free_throw_percentage") {
    data = `${statistics[teamData].free_throw_percentage}%`;
  }

  if (key === "free_throw_percentage_number") {
    data = statistics[teamData].free_throw_percentage;
  }

  if (key === "number_of_pauses_remaining") {
    data = statistics[teamData].number_of_pauses_remaining;
  }

  if (key === "total_pauses") {
    data = statistics[teamData].total_pauses;
  }

  if (key === "assists") {
    data = statistics[teamData].assists;
  }

  if (key === "blocks") {
    data = statistics[teamData].blocks;
  }

  if (key === "steals") {
    data = statistics[teamData].steals;
  }

  if (key === "rebounds") {
    data = statistics[teamData].rebounds;
  }

  if (key === "offensive_rebounds") {
    data = statistics[teamData].offensive_rebounds;
  }

  if (key === "defensive_rebounds") {
    data = statistics[teamData].defensive_rebounds;
  }

  if (key === "turnovers") {
    data = statistics[teamData].turnovers;
  }

  if (key === "fouls") {
    data = statistics[teamData].fouls;
  }

  return data;
};

export const getProcessedStats = (
  match: IMatch,
  statistics: IStatistics,
  isDefault: boolean
) => {
  const techKind: Record<string, string> = StatsEnum.techKind;

  return Object.keys(techKind)
    .map((forwardKey) => {
      let key = forwardKey;
      if (forwardKey === "hits_shots") {
        key = "max_hits_shots";
      }

      if (forwardKey === "three_point") {
        key = "max_three_point";
      }

      if (forwardKey === "free_throw_goal") {
        key = "max_free_throw_goal";
      }

      let showAwayPre = calculatePercentage(
        Number(chooseValue(statistics, key, 2)),
        Number(chooseValue(statistics, key, 1)) +
          Number(chooseValue(statistics, key, 2))
      );
      let showHomePre = calculatePercentage(
        Number(chooseValue(statistics, key, 1)),
        Number(chooseValue(statistics, key, 1)) +
          Number(chooseValue(statistics, key, 2))
      );
      let winHome = Number(chooseValue(statistics, key, 1));
      let winAway = Number(chooseValue(statistics, key, 2));
      if (key === "hits_shots_percentage") {
        showAwayPre = chooseValue(statistics, "hits_shots_percentage", 2);

        showHomePre = chooseValue(statistics, "hits_shots_percentage", 1);

        winHome = Number(
          chooseValue(statistics, "hits_shots_percentage_number", 1)
        );
        winAway = Number(
          chooseValue(statistics, "hits_shots_percentage_number", 2)
        );
      }
      if (key === "three_point_percentage") {
        showAwayPre = chooseValue(statistics, "three_point_percentage", 2)

        showHomePre = chooseValue(statistics, "three_point_percentage", 1)

        winHome = Number(
          chooseValue(statistics, "three_point_percentage_number", 1)
        );
        winAway = Number(
          chooseValue(statistics, "three_point_percentage_number", 2)
        );
      }
      if (key === "free_throw_percentage") {
        showAwayPre = chooseValue(statistics, "free_throw_percentage", 2)

        showHomePre = chooseValue(statistics, "free_throw_percentage", 1)

        winHome = Number(
          chooseValue(statistics, "free_throw_percentage_number", 1)
        );
        winAway = Number(
          chooseValue(statistics, "free_throw_percentage_number", 2)
        );
      }

      return {
        key,
        type: techKind[forwardKey],
        home: chooseValue(statistics, forwardKey, 1),
        away: chooseValue(statistics, forwardKey, 2),
        showAwayPre,
        showHomePre,
        winHome: winHome > winAway ? "as-statistics__home-win" : "",
        winAway: winAway > winHome ? "as-statistics__away-win" : "",
      };
    })
    .filter((stat) => {
      return (
        (
          (stat.home !== 0 || stat.away !== 0)
           &&
          (stat.home !== "0%" || stat.away !== "0%")
        ) || isDefault === true
      );
    });
};

export const initializeData = () => ({
  score: { home: 0, away: 0 },
  homeData: {
    hits_shots: 0,
    total_hits_shots: 0,
    hits_shots_percentage: 0,
    free_throw_goal: 0,
    total_free_throw_goal: 0,
    free_throw_percentage: 0,
    number_of_pauses_remaining: 0,
    total_pauses: 0,
    three_point: 0,
    total_three_point: 0,
    three_point_percentage: 0,
    assists: 0,
    blocks: 0,
    steals: 0,
    rebounds: 0,
    offensive_rebounds: 0,
    defensive_rebounds: 0,
    turnovers: 0,
    fouls: 0,
  },
  awayData: {
    hits_shots: 0,
    total_hits_shots: 0,
    hits_shots_percentage: 0,
    free_throw_goal: 0,
    total_free_throw_goal: 0,
    free_throw_percentage: 0,
    number_of_pauses_remaining: 0,
    total_pauses: 0,
    three_point: 0,
    total_three_point: 0,
    three_point_percentage: 0,
    assists: 0,
    blocks: 0,
    steals: 0,
    rebounds: 0,
    offensive_rebounds: 0,
    defensive_rebounds: 0,
    turnovers: 0,
    fouls: 0,
  },
  id: "",
  current_type: 0,
  type: 10,
});

export const mapPlayer = (playerId: string, listPlayer: IPlayer[]) => {
  if (!listPlayer) {
    return null;
  }
  return listPlayer.find((player) => player.id === playerId);
};

const groupShootingPointsByPartOfGame = (
  stp: IShootingPoints | undefined | any
) => {
  if (!stp?.results) {
    return null;
  }
  const groupPartOfGame = stp.results.reduce((acc: any, item: any) => {
    if (!acc[item[7]]) {
      // 7 is part of game (bar, quarter, half)
      acc[item[7]] = [];
    }
    acc[item[7]].push(item);
    return acc;
  }, {});
  return groupPartOfGame;
};

const reverseArray = (data: any): Array<any> => {
  const t = [];
  for (const key in data) {
    const s = data[key].reverse();
    t.push(...s);
  }
  return t;
};

const calculateResponseSums = (jsonData: Array<any>) => {
  return jsonData.map((item, i) => {
    let sumAway = 0;
    let sumHome = 0;
    for (let j = 0; j <= i; j++) {
      if (jsonData[j][0] === 1) {
        sumHome += jsonData[j][4];
      }
      if (jsonData[j][0] === 2) {
        sumAway += jsonData[j][4];
      }
    }
    return [...item, sumHome, sumAway];
  });
};

export const convertShootingPointsData = (
  data: IShootingPointResponseData
): any => {
  if (!data) {
    return null;
  }
  const { shootingPoints } = data;
  if (!shootingPoints) {
    return null;
  }
  const groupShootingPoints = groupShootingPointsByPartOfGame(shootingPoints);
  const arrReversed = reverseArray(groupShootingPoints);
  const sumMatch = calculateResponseSums(arrReversed);

  const result = groupShootingPointsByPartOfGame({ results: sumMatch });
  return result;
};

const calculateShootingPointsOfPlayer = (
  objectData: any[]
): IPlayerDataShooting[] => {
  const result = [];
  for (const property in objectData) {
    const value = objectData[property];
    let totalPont = 0;
    value.map((item: Array<any>) => {
      let point = item[4];
      totalPont += point;
    });
    result.push({
      id: property,
      teamId: value[0][0],
      scoreOfSorting: value[0][4],
      x: value[0][5],
      y: value[0][6],
      numberOfBar: value[0][7],
      time: value[0][8],
      totalPoint: totalPont,
    });
  }
  return result;
};

export const groupByPlayer = (data: IShootingPoints | null | undefined) => {
  if (!data) {
    return null;
  }
  const dataGroupByPlayer = data?.results.reduce((acc: any, item: any) => {
    if (!acc[item[1]]) {
      acc[item[1]] = [];
    }
    acc[item[1]].push(item);
    return acc;
  }, {});
  const jsonShootingPoints = calculateShootingPointsOfPlayer(
    dataGroupByPlayer
  ).sort((a, b) => ((b.totalPoint || 0) - (a.totalPoint || 0) >= 0 ? 1 : -1));
  return jsonShootingPoints;
};
